import { useState } from 'react'
import { Eye, EyeOff, ExternalLink, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface ConfigFormProps {
  config: any
  onGuardar: (datos: any) => Promise<void>
}

export function ConfigForm({ config, onGuardar }: ConfigFormProps) {
  const [formData, setFormData] = useState({
    account_sid: config?.account_sid || '',
    auth_token: config?.auth_token || '',
    whatsapp_number: config?.whatsapp_number || '',
    verified_name: config?.verified_name || '',
    business_account_id: config?.business_account_id || ''
  })

  const [showToken, setShowToken] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showSidInfo, setShowSidInfo] = useState(false)
  const [showNumberInfo, setShowNumberInfo] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await onGuardar(formData)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="account_sid">Account SID</Label>
          <button
            type="button"
            onClick={() => setShowSidInfo(!showSidInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-1"
          >
            <Info className="h-4 w-4" />
          </button>
          {showSidInfo && (
            <span className="text-muted-foreground ml-2 text-xs animate-in slide-in-from-left-2 duration-200">
              Encuéntralo en la{' '}
              <a
                href="https://console.twilio.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-900 hover:underline inline-flex items-center gap-1"
              >
                consola de Twilio
                <ExternalLink className="h-3 w-3" />
              </a>
            </span>
          )}
        </div>
        <Input
          id="account_sid"
          type="text"
          name="account_sid"
          value={formData.account_sid}
          onChange={handleChange}
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="auth_token">Auth Token</Label>
        <div className="relative">
          <Input
            id="auth_token"
            type={showToken ? 'text' : 'password'}
            name="auth_token"
            value={formData.auth_token}
            onChange={handleChange}
            placeholder="••••••••••••••••••••••••••••••••"
            className="pr-10"
            required
          />
          <button
            type="button"
            onClick={() => setShowToken(!showToken)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="whatsapp_number">Número de WhatsApp</Label>
          <button
            type="button"
            onClick={() => setShowNumberInfo(!showNumberInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-1"
          >
            <Info className="h-4 w-4" />
          </button>
          {showNumberInfo && (
            <span className="text-muted-foreground ml-2 text-xs animate-in slide-in-from-left-2 duration-200">
              Incluye el código de país con +
            </span>
          )}
        </div>
        <Input
          id="whatsapp_number"
          type="text"
          name="whatsapp_number"
          value={formData.whatsapp_number}
          onChange={handleChange}
          placeholder="+14155238886"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="verified_name">Nombre Verificado (Opcional)</Label>
        <Input
          id="verified_name"
          type="text"
          name="verified_name"
          value={formData.verified_name}
          onChange={handleChange}
          placeholder="Tonal Latam"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="business_account_id">Business Account ID (Opcional)</Label>
        <Input
          id="business_account_id"
          type="text"
          name="business_account_id"
          value={formData.business_account_id}
          onChange={handleChange}
          placeholder="WABA ID"
        />
      </div>

      <Button
        type="submit"
        className="w-full bg-black hover:bg-gray-800 text-white"
        disabled={loading}
      >
        {loading ? 'Guardando...' : 'Guardar Configuración'}
      </Button>
    </form>
  )
}
